import { useMemo } from 'react';
import type { DocumentAccess, UserRoles } from '../base';
import type { UserId } from '../types';
import { objectValues } from '../utils';

export type DocumentAccessManager = {
  getForUser: (userId: UserId) => DocumentAccess | undefined;
  updateForUsers: (userId: UserId[], role: UserRoles) => DocumentAccess[];
  getUserAccess: (userId: UserId) => {
    atLeastOwner: () => boolean;
    atLeastMember: () => boolean;
    atLeastGuest: () => boolean;
  };
};

export const useDocumentAccessManager = (usersAccess?: DocumentAccess[]) => {
  return useMemo<DocumentAccessManager | undefined>(() => {
    if (!usersAccess) return;

    // Create dictionary based on user id
    const dictionary = usersAccess.reduce((acc, model) => {
      acc[model.userID] = {
        roles: model.roles,
        userID: model.userID,
      };

      return acc;
    }, {} as Record<UserId, DocumentAccess>);

    return {
      getForUser(userId: UserId) {
        return dictionary[userId];
      },
      updateForUsers(userIds: UserId[], role: UserRoles) {
        userIds.forEach(id => {
          if (role) {
            dictionary[id] = {
              roles: [role],
              userID: id,
            };
          } else {
            delete dictionary[id];
          }
        });

        return objectValues(dictionary);
      },
      getUserAccess(userId: UserId) {
        const user = this.getForUser(userId);

        return {
          atLeastOwner() {
            return !!user && user.roles.includes('owner');
          },
          atLeastMember() {
            return !!user && user.roles.includes('member');
          },
          atLeastGuest() {
            return !!user && user.roles.includes('guest');
          },
        };
      },
    };
  }, [usersAccess]);
};
